import { Icon } from '@components/common/generic-icon';
import { useI18NextContext } from '@components/hooks';
import { TestId } from '@components/test-ids';
import styled from '@emotion/styled';
import { Grid } from '@mui/material';
import { Climate as GeographyClimate } from '@model/contentful';
import { breakpoints, mq } from '@styles/breakpoints';
import { mixins } from '@styles/mixins';
import { withTheme } from '@emotion/react';
import React, { useState } from 'react';
import { Slider } from './slider/Slider';
import { ClimateType } from '@model/iceberg';
import { Months, monthsWeight } from '@model/common/dates';
import { LazyLoadBackgroundImage } from '@components/common/lazy-load';
import { SelectableThemeProps, ThemeProps } from '@theme/base';

export interface ClimateProps {
  climate: Array<GeographyClimate>;
  image?: string;
  className?: string;
}

const Content: any = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  flexGrow: 1,
  padding: `${theme.custom.spacing.xxLarge}px ${theme.custom.spacing.large}px`,
  zIndex: 1,
  [mq.ie()]: {
    margin: `${theme.custom.spacing.large}px 0`
  },

  [mq.ie(breakpoints.medium)]: {
    margin: `${theme.custom.spacing.xxLarge}px 0`
  },

  [mq.small]: {
    padding: `${theme.custom.spacing.xxxLarge}px ${theme.custom.spacing.large}px`
  }
}));

const Container = styled.div(({ theme }: ThemeProps) => ({
  backgroundColor: theme.custom.colors.overlay.dark,
  backdropFilter: 'blur(10px)',
  borderRadius: theme.custom.spacing.small,
  display: 'flex',
  justifyContent: 'center',
  padding: 20,
  color: theme.custom.colors.white,
  ...(theme.custom.typography.paragraph as any),
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
  paddingBottom: 0,
  [mq.small]: {
    padding: 40,
    paddingBottom: 0
  }
}));

const TitleRow = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: theme.custom.spacing.xxLarge
}));

const Title = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h2 as any),
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  ...mixins(theme).truncate
}));

const DegreesContainer = styled.span(({ theme }: ThemeProps) => ({
  display: 'flex',
  ...(theme.custom.typography.h2 as any),
  marginLeft: theme.custom.spacing.xSmall
}));

const Sign = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h4 as any)
}));

const MonthsRow = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flexWrap: 'wrap',
  marginBottom: 0,
  [mq.medium]: {
    marginBottom: theme.custom.spacing.large * 2
  }
}));

const MonthColumn = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '16%',
  marginBottom: theme.custom.spacing.xLarge,
  [mq.medium]: {
    flex: 1,
    width: '100%',
    marginBottom: 0
  }
}));

const TabItems: any = styled.div({
  display: 'flex',
  flexDirection: 'row'
});

interface TabProps extends SelectableThemeProps {
  percent?: number;
}

export const TabItem: any = styled.div(({ theme, isSelected, percent }: TabProps) => ({
  width: `${percent}%`,
  color: isSelected ? theme.custom.colors.group1.base : 'inherit',
  transition: `color ${theme.custom.transitions.default}`,
  textAlign: 'center',
  cursor: 'pointer',
  paddingBottom: theme.custom.spacing.large,
  ':hover': {
    color: theme.custom.colors.group1.base
  },
  [mq.small]: {
    paddingBottom: 40
  }
}));

const TabText = styled.div(({ theme }: ThemeProps) => ({
  display: 'none',
  ...mixins(theme).truncate,
  [mq.medium]: {
    display: 'block',
    fontWeight: 500
  }
}));

const TabIcon: any = styled.div(({ theme, isSelected }: TabProps) => ({
  display: 'block',
  i: {
    color: isSelected ? theme.custom.colors.group1.base : theme.custom.colors.white,
    fontSize: '3rem'
  },
  ':hover i': {
    color: theme.custom.colors.group1.base,
    transition: `color ${theme.custom.transitions.default}`
  },
  [mq.medium]: {
    display: 'none'
  }
}));

const titleIconStyles = {
  fontSize: '3.5rem',
  color: 'white',
  display: 'none',
  [mq.medium]: {
    display: 'block'
  }
};

const SignSmall = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  top: 0,
  right: -8,
  position: 'absolute'
}));

const Month = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  textTransform: 'uppercase',
  marginBottom: theme.custom.spacing.xSmall * 2,
  [mq.medium]: {
    marginBottom: theme.custom.spacing.large
  }
}));

const MonthUnit = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 30,
  width: '100%',
  [mq.medium]: {
    ...(theme.custom.typography.h5 as any)
  }
}));

const MonthValue = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  display: 'flex',
  justifyContent: 'center',
  position: 'relative'
}));

const AverageTitle = styled.div({
  display: 'flex'
});

const ClimateContainer: any = styled.div<any>({
  width: '100vw',
  maxWidth: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
  display: 'flex',
  justifyContent: 'flex-start',
  flexDirection: 'column',
  alignItems: 'center',
  [mq.ie()]: {
    minHeight: 0
  }
});

const gridItemStyles = {
  flexGrow: 1
};

const iconMap: Record<ClimateType, string> = {
  [ClimateType.SUNSHINE]: 'mhi-sun',
  [ClimateType.RAINFALL]: 'mhi-rainfall',
  [ClimateType.TEMPERATURE]: 'mhi-temprature'
};

export const ClimateComponent = ({ climate: climateData, image }: ClimateProps) => {
  if (!climateData?.length) return null;

  const climate = climateData.filter(({ months }) => !!months);

  const [selected, setSelected] = useState(climate[0].title);
  const count: number = climate.length;
  const selectedIndex: number = climate.findIndex((data: GeographyClimate) => data.title === selected);
  const percent: number = (1 / count) * 100;
  const selectedTab: GeographyClimate = climate.find((tab: GeographyClimate) => tab.title === selected) || climate[0];

  const t: any = useI18NextContext();

  const renderMonths = () => (
    <MonthsRow>
      {Object.entries(selectedTab.months)
        .sort((a: [string, number], b: [string, number]) => monthsWeight[a[0] as Months] - monthsWeight[b[0] as Months])
        .map(([key, value]: any) => (
          <MonthColumn key={key}>
            <Month>{key}</Month>
            <MonthUnit>
              <MonthValue>
                {value}
                {selectedTab.degrees && <SignSmall>&deg;</SignSmall>}
              </MonthValue>
            </MonthUnit>
          </MonthColumn>
        ))}
    </MonthsRow>
  );

  const renderTabs = () => (
    <TabItems>
      {climate.map((tab: GeographyClimate) => (
        <TabItem
          key={tab.title}
          isSelected={tab.title === selected}
          onClick={() => setSelected(tab.title)}
          percent={percent}
        >
          <TabText>{tab.title}</TabText>
          <TabIcon isSelected={tab.title === selected}>
            <Icon name={iconMap[tab.type as ClimateType]} />
          </TabIcon>
        </TabItem>
      ))}
    </TabItems>
  );

  return (
    <ClimateContainer>
      {image && <LazyLoadBackgroundImage src={image} />}
      <Content data-testid={TestId.climate}>
        <Grid container justifyContent={'center'}>
          <Grid item sm={12} md={10} lg={8} xl={6} style={gridItemStyles}>
            <Container>
              <AverageTitle>{t('common__average--title')}</AverageTitle>
              <TitleRow>
                <Title>
                  {selectedTab.title}
                  {selectedTab.degrees && (
                    <DegreesContainer>
                      <Sign>&deg;</Sign>C
                    </DegreesContainer>
                  )}
                </Title>
                <Icon name={iconMap[selectedTab.type]} css={titleIconStyles} />
              </TitleRow>
              {renderMonths()}
              {count > 1 && <Slider count={count} selected={selectedIndex} />}
              {renderTabs()}
            </Container>
          </Grid>
        </Grid>
      </Content>
    </ClimateContainer>
  );
};

export const Climate = withTheme(ClimateComponent);
